<script setup>
import { ref, watch, computed, onMounted, nextTick, onBeforeMount } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Button from '../../../../../components-next/button/Button.vue';
import UserDataFields from './UserDataFields.vue';

const emit = defineEmits(['update', 'validation', 'show-attribute-info']);
const store = useStore();

const props = defineProps({
  initialUserFields: {
    type: Array,
    default: () => ([]) // Inicia vazio, campos são opcionais
  },
  isEditMode: {
    type: Boolean,
    default: false
  }
});

// Buscar custom attributes do sistema
onBeforeMount(() => {
  store.dispatch('attributes/get');
});

// Computed para pegar os custom attributes de contato (APENAS TEXTO)
const availableAttributes = computed(() => {
  const attributes = store.getters['attributes/getContactAttributes'];
  
  // Garantir que sempre retorna um array
  if (!attributes || !Array.isArray(attributes)) {
    return [];
  }
  
  // FILTRAR APENAS ATRIBUTOS DE TEXTO
  return attributes.filter(attr => 
    attr && attr.attributeDisplayType === 'text'
  );
});

// Se não estiver em modo de edição e não houver campos iniciais, não adiciona campo padrão
const getInitialFields = () => {
  // Não adiciona campo padrão automaticamente
  // O usuário deve escolher ou criar os campos que deseja
  return [...props.initialUserFields];
};

const userFields = ref(getInitialFields());
const userFieldsValid = ref(true); // Inicializa como true pois campos são opcionais

// Função para gerar a key baseada no nome
const generateKey = (name) => {
  if (!name) return '';
  // Converte para minúsculas e substitui espaços por underscores
  return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
};

// Garantir que os campos iniciais tenham keys
const initializeKeys = () => {
  userFields.value.forEach(field => {
    if (field.name && !field.key) {
      field.key = generateKey(field.name);
    }
  });
};

// Observar mudanças e emitir atualizações para o componente pai
watch(userFields, (newValue) => {
  emit('update', [...newValue]);
}, { deep: true });

// Quando as props iniciais mudam, atualiza os valores locais
watch(() => props.initialUserFields, (newValue) => {
  if (JSON.stringify(newValue) !== JSON.stringify(userFields.value)) {
    // Apenas copia os valores recebidos
    userFields.value = [...newValue];
    initializeKeys();
  }
}, { deep: true });

const addUserField = () => {
  userFields.value.unshift({
    name: '',
    description: '',
    required: false,
    key: ''
  });
  
  // Revalida imediatamente após adicionar
  const isValid = userFields.value.length === 0 || userFields.value.every(field => 
    field.name && field.name.trim()
  );
  emit('validation', isValid);
};

// Função para sincronizar campos com atributos do sistema ao editar
const syncUserFieldsWithSystemAttributes = async () => {
  if (!props.isEditMode || userFields.value.length === 0) return;
  
  try {
    // Aguardar atributos serem carregados primeiro
    await store.dispatch('attributes/get');
    
    const attributes = availableAttributes.value || [];
    const attributesByKey = new Map(attributes.map(attr => [attr.attributeKey, attr]));
    
    // Para cada campo do agente, verificar se existe no sistema
    for (const field of userFields.value) {
      if (!field.key) continue;
      
      const systemAttribute = attributesByKey.get(field.key);
      
      if (!systemAttribute) {
        // Atributo não existe no sistema, criar automaticamente sem perguntar
        try {
          await store.dispatch('attributes/create', {
            attribute_display_name: field.name,
            attribute_display_type: 0, // text
            attribute_description: field.description || '',
            attribute_key: field.key,
            attribute_model: 1 // contact_attribute
          });
          
          console.log(`Atributo '${field.name}' criado automaticamente no sistema`);
        } catch (error) {
          // Se falhar ao criar (ex: já existe), apenas logar
          console.log(`Atributo '${field.name}' já pode existir ou erro ao criar:`);
        }
      }
    }
    
    // Recarregar atributos após possíveis criações
    await store.dispatch('attributes/get');
  } catch (error) {
    console.error('Erro ao sincronizar atributos:', error);
  }
};

const updateUserFields = async (fields) => {
  userFields.value = fields;
  
  // Garantir que todos os campos tenham keys
  userFields.value.forEach(field => {
    if (field.name && (!field.key || field.key !== generateKey(field.name))) {
      field.key = generateKey(field.name);
    }
  });
  
  // Aguarda o próximo ciclo antes de validar
  await nextTick();
  
  // Revalidar após atualização (importante quando campos são removidos)
  const isValid = userFields.value.length === 0 || userFields.value.every(field => 
    field.name && field.name.trim()
  );
  userFieldsValid.value = isValid;
  
  // IMPORTANTE: Emitir o resultado da validação para o componente pai
  emit('validation', isValid);
};

const handleFieldsValidation = (isValid) => {
  userFieldsValid.value = isValid;
  emit('validation', isValid);
};

// Emitir estado de validação inicial na montagem
onMounted(async () => {
  // Inicializa as keys dos campos
  initializeKeys();
  
  // Sincronizar com atributos do sistema se estiver em modo de edição
  // Isso criará automaticamente qualquer atributo que ainda não existe
  if (props.isEditMode && userFields.value.length > 0) {
    await syncUserFieldsWithSystemAttributes();
  }
  
  // Validar campos iniciais
  const isValid = userFields.value.length === 0 || userFields.value.every(field => 
    field.name && field.name.trim()
  );
  
  // Emitir após o próximo ciclo de renderização
  nextTick(() => {
    emit('validation', isValid);
    // Emitir os campos atualizados também
    emit('update', [...userFields.value]);
  });
});

const hasFields = computed(() => {
  return userFields.value.length > 0;
});

// Função para solicitar abertura do modal de informação ao componente pai
const requestShowAttributeInfo = () => {
  emit('show-attribute-info');
};
</script>

<template>
  <div class="space-y-6">
    <!-- Dados que serão perguntados ao usuário -->
    <div class="pt-2">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-base font-medium text-n-slate-12 flex items-center">
          <span class="i-lucide-user mr-2"></span>
          Atributos personalizados
          <!-- Botão de informação -->
          <button
            @click="requestShowAttributeInfo"
            class="ml-2 p-1 rounded-full hover:bg-n-slate-3 transition-colors"
            title="Informações sobre atributos personalizados"
          >
            <svg class="w-4 h-4 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </button>
        </h3>
        <Button
          label="Adicionar Atributo"
          icon="i-lucide-plus"
          variant="outline"
          size="sm"
          @click="addUserField"
        />
      </div>
      
      <!--<p class="text-sm text-n-slate-11 mb-4">
        Configure os campos que devem ser solicitados aos usuários durante a conversa. Você pode definir quais campos são obrigatórios ou opcionais.
      </p>-->
      
      <div v-if="hasFields">
        <UserDataFields
          :items="userFields"
          :availableAttributes="availableAttributes"
          name-label="Atributo"
          required-label="Campo Obrigatório"
          @update="updateUserFields"
          @validation="handleFieldsValidation"
        />
      </div>
      <div v-else class="text-sm text-n-slate-11 italic bg-n-slate-2 dark:bg-n-slate-12/5 rounded-lg p-4 border border-n-slate-5 dark:border-n-slate-10/20">
        <div class="flex items-center gap-2">
          <span class="i-lucide-info w-4 h-4 text-n-slate-9"></span>
          <span>Nenhum atributo definido. Clique em "Adicionar Atributo" para configurar os atributos que o agente deve coletar.</span>
        </div>
      </div>
    </div>
  </div>
</template>